import React, { useState, useRef, useEffect } from 'react';
import { DateRange, ViewWeek, CalendarMonth, CalendarViewMonth, AllInclusive, KeyboardArrowDown } from '@mui/icons-material';
import type { TimeGranularity } from '../utils/timeGranularity';

/** Configuration for each granularity option */
const GRANULARITY_CONFIG: Record<TimeGranularity, { icon: React.ElementType; label: string; shortLabel: string }> = {
  Month:    { icon: DateRange,          label: 'Monthly',        shortLabel: 'Mo' },
  Quarter:  { icon: CalendarViewMonth,  label: 'Quarterly',      shortLabel: 'Qtr' },
  Terminal: { icon: ViewWeek,           label: 'By Terminal',    shortLabel: 'Term' },
  Yearly:   { icon: CalendarMonth,      label: 'Yearly',         shortLabel: 'Yr' },
  Full:     { icon: AllInclusive,       label: 'Full Horizon',   shortLabel: 'Full' },
};

export interface TimeGranularityToggleProps {
  /** Current selected granularity */
  value: TimeGranularity;
  /** Callback when user selects a different granularity */
  onChange: (granularity: TimeGranularity) => void;
  /** Which options to show (order is preserved). Defaults to ['Month', 'Terminal'] */
  options?: TimeGranularity[];
  /** Override default labels for specific options, e.g. { Yearly: 'Full' } */
  labels?: Partial<Record<TimeGranularity, string>>;
  /** When true, shows the active option as a non-interactive pill (no dropdown) */
  disabled?: boolean;
}

/**
 * Compact dropdown selector for time granularity.
 * Shows the active option as a pill button; click to open a dropdown with all options.
 * Caller controls which options appear via the `options` prop.
 */
export const TimeGranularityToggle: React.FC<TimeGranularityToggleProps> = ({
  value,
  onChange,
  options = ['Month', 'Terminal'],
  labels,
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const active = GRANULARITY_CONFIG[value];
  const ActiveIcon = active.icon;
  const activeLabel = labels?.[value] ?? active.shortLabel;

  // Disabled: show non-interactive pill with lock icon feel
  if (disabled || options.length <= 1) {
    return (
      <div className="flex items-center bg-gray-100 rounded-full p-1 opacity-60 cursor-not-allowed">
        <span className="px-3 py-1.5 rounded-full flex items-center gap-1.5 text-xs font-medium text-gray-500">
          <ActiveIcon className="w-4 h-4" />
          <span>{activeLabel}</span>
        </span>
      </div>
    );
  }

  return (
    <div ref={ref} className="relative flex items-center bg-gray-100 rounded-full p-1">
      <button
        onClick={() => setOpen(!open)}
        className={`px-3 py-1.5 rounded-full transition-colors flex items-center gap-1.5 text-xs font-medium ${
          open
            ? 'bg-white text-gray-900 shadow-sm'
            : 'text-gray-700 hover:text-gray-900'
        }`}
      >
        <ActiveIcon className="w-4 h-4" />
        <span>{activeLabel}</span>
        <KeyboardArrowDown
          className="w-3.5 h-3.5 text-gray-400"
          style={{ transition: 'transform 0.15s', transform: open ? 'rotate(180deg)' : 'rotate(0)' }}
        />
      </button>

      {/* Dropdown */}
      {open && (
        <div
          className="absolute right-0 top-full mt-1 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50 min-w-[150px]"
          style={{ animation: 'fadeIn 0.1s ease-out' }}
        >
          {options.map((opt) => {
            const { icon: Icon, label: defaultLabel } = GRANULARITY_CONFIG[opt];
            const label = labels?.[opt] ?? defaultLabel;
            const isActive = value === opt;
            return (
              <button
                key={opt}
                onClick={() => { onChange(opt); setOpen(false); }}
                className={`w-full flex items-center gap-2.5 px-3 py-2 text-xs transition-colors ${
                  isActive
                    ? 'bg-blue-50 text-blue-700 font-medium'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`}
              >
                <Icon className={`w-4 h-4 ${isActive ? 'text-blue-500' : 'text-gray-400'}`} />
                <span>{label}</span>
                {isActive && <span className="ml-auto text-blue-400">&#10003;</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};
